import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  useTheme,
  MenuItem
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddIcon from "@mui/icons-material/Add";
import FormModal, { formFieldSx } from "../common/FormModal/FormModal";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { useCreateProduct } from "../../hooks/api/useProduct";
import { useGetSupplierList } from "../../hooks/api/useSupplier";
import { useGetCategoryList } from "../../hooks/api/useCategory";
import { getLeafCategories } from "../Category/utils/categoryUtils";
import { ProductFormData } from "./types/products";
import { DefaultProductFormValues } from "./constants/products";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation,
  AlertBannerContext,
  usePlatformConfig,
  resolvePlatformEnumCatalog
} from "@kivunova/kivufrontendcommon";
import { useCompanyStores } from "../../hooks/api/useCompanyStores";
import {
  UNIT_DISPLAY_LABELS,
  SELLING_MODE_DISPLAY_LABELS
} from "./constants/displayLabels";
import useMediaQuery from "@mui/material/useMediaQuery";
import { SellingMode } from "./enums/sellingMode";
import { Unit } from "./enums/unit";
import { RootState } from "../../store/store";
import { resetSupplierList } from "../../store/slices/supplierSlice";
import { CATEGORY_STATUS } from "../Category/enums/categoryEnums";
import {
  getAllowedUnitsForSellingMode,
  getAutoSelectedUnit,
  isBaseUnitLocked,
  isPartialSellingPermitted
} from "./utils/sellingModeUtils";
import { buildProductFormSchema } from "./utils/buildProductFormSchema";
import { isUserCompanyAdmin } from "../../hooks/api/useProductItem";
import QuickAddSupplierModal from "./QuickAddSupplierModal";
import QuickAddCategoryModal from "./QuickAddCategoryModal";
import AddProductGuidanceBanner from "./AddProductGuidanceBanner";

interface AddProductModalProps {
  open: boolean;
  onClose: () => void;
}

const AddProductModal: React.FC<AddProductModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const createProduct = useCreateProduct();
  const { mutateAsync: fetchSupplierList, isPending: supplierListPending } =
    useGetSupplierList();
  const getCategoryList = useGetCategoryList();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();
  const { setAlert } = useContext(AlertBannerContext);
  const { sellingMode: platformSellingModes, baseUnit: platformBaseUnits } =
    usePlatformConfig();

  const allowedSellingModes = useMemo(
    () =>
      resolvePlatformEnumCatalog(
        platformSellingModes,
        Object.values(SellingMode)
      ),
    [platformSellingModes]
  );

  const allowedBaseUnits = useMemo(
    () => resolvePlatformEnumCatalog(platformBaseUnits, Object.values(Unit)),
    [platformBaseUnits]
  );

  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const isCompanyAdmin = isUserCompanyAdmin(userDetails);
  const companyId = userDetails?.companyId?.trim() ?? "";
  const userStoreId = String(userDetails?.storeId ?? "").trim();

  const [quickAddSupplierOpen, setQuickAddSupplierOpen] = useState(false);
  const [quickAddCategoryOpen, setQuickAddCategoryOpen] = useState(false);
  const prevSupplierScopeRef = useRef("");
  /** Tracks last supplier fetch scope to clear Redux when company admin switches store. */
  const supplierFetchScopeRef = useRef("");

  const { stores: companyStores, loading: storesLoading } = useCompanyStores({
    enabled: open && isCompanyAdmin,
    companyId: companyId || undefined,
    setAlert
  });

  const supplierList = useSelector(
    (state: RootState) => state.suppliers.supplierList
  );
  const categoryList = useSelector(
    (state: RootState) => state.categories.categoryList
  );

  const validationSchema = useMemo(
    () =>
      buildProductFormSchema(t, {
        requireStoreForCompanyAdmin: isCompanyAdmin,
        allowedSellingModes
      }),
    [t, isCompanyAdmin, allowedSellingModes]
  );

  useEffect(() => {
    if (!open) {
      setQuickAddSupplierOpen(false);
      setQuickAddCategoryOpen(false);
      return;
    }
    prevSupplierScopeRef.current = "";
    getCategoryList
      .mutateAsync({ status: CATEGORY_STATUS.ACTIVE })
      .catch(() => {});
  }, [open]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue
  } = useForm<ProductFormData>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: DefaultProductFormValues
  });

  const selectedSellingMode = watch("sellingMode") as SellingMode | undefined;
  const watchedStoreId = watch("storeId");

  const supplierScopeStoreId = isCompanyAdmin
    ? String(watchedStoreId ?? "").trim()
    : userStoreId;

  useEffect(() => {
    if (!open) {
      supplierFetchScopeRef.current = "";
      return;
    }
    if (!supplierScopeStoreId) {
      if (isCompanyAdmin) {
        dispatch(resetSupplierList());
      }
      return;
    }
    const prevScope = supplierFetchScopeRef.current;
    supplierFetchScopeRef.current = supplierScopeStoreId;
    if (isCompanyAdmin && prevScope !== supplierScopeStoreId) {
      dispatch(resetSupplierList());
    }
    fetchSupplierList({ storeId: supplierScopeStoreId }).catch(() => {});
  }, [open, supplierScopeStoreId, isCompanyAdmin]);

  useEffect(() => {
    if (!open || !isCompanyAdmin) return;
    const sid = String(watchedStoreId ?? "").trim();
    const prev = prevSupplierScopeRef.current;
    if (prev && prev !== sid) {
      setValue("supplierId", "");
    }
    prevSupplierScopeRef.current = sid;
  }, [open, isCompanyAdmin, watchedStoreId, setValue]);

  /**
   * When sellingMode changes:
   * - Auto-select UNIT for SINGLE_ITEM and UNIT_BASED (the only valid unit).
   * - Clear baseUnit for MEASURED so the user picks an appropriate measured unit.
   * - Lock isPartialSellingAllowed to false for modes that do not permit it.
   */
  useEffect(() => {
    const autoUnit = getAutoSelectedUnit(selectedSellingMode);
    if (autoUnit !== undefined) {
      setValue("baseUnit", autoUnit, { shouldValidate: true });
    } else {
      setValue("baseUnit", undefined, { shouldValidate: false });
    }

    if (!isPartialSellingPermitted(selectedSellingMode)) {
      setValue("isPartialSellingAllowed", false, { shouldValidate: false });
    }
  }, [selectedSellingMode, setValue]);

  const onSubmit: SubmitHandler<ProductFormData> = async (data) => {
    try {
      await createProduct.mutateAsync({ ...data });
      reset(DefaultProductFormValues);
      onClose();
    } catch (err) {
      console.error("❌ Failed to create product:", err);
    }
  };

  const isLoading = isSubmitting || createProduct.isPending;

  /** Render a plain text field. */
  const renderField = (
    name: keyof ProductFormData,
    label: string,
    placeholder?: string,
    inputProps?: { "data-testid"?: string }
  ) => {
    const isDescription = name === "description";

    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            fullWidth
            size={matchMD ? "small" : "medium"}
            label={label}
            placeholder={placeholder}
            type="text"
            multiline={isDescription}
            rows={isDescription ? 3 : 1}
            error={!!errors[name]}
            helperText={errors[name]?.message}
            disabled={isLoading}
            sx={formFieldSx}
            inputProps={inputProps}
          />
        )}
      />
    );
  };

  /** Render a select field for enum options. */
  const renderSelectField = (
    name: keyof ProductFormData,
    label: string,
    options: { value: string | number; label: string }[]
  ) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          select
          fullWidth
          size={matchMD ? "small" : "medium"}
          label={label}
          error={!!errors[name]}
          helperText={errors[name]?.message}
          disabled={isLoading}
          sx={formFieldSx}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );

  /**
   * Render a boolean field rendered as a Yes/No select.
   * Accepts an optional `locked` flag to enforce selling-mode constraints.
   */
  const renderBooleanField = (
    name: keyof ProductFormData,
    label: string,
    locked?: boolean
  ) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          select
          fullWidth
          size={matchMD ? "small" : "medium"}
          label={label}
          value={field.value ? "true" : "false"}
          onChange={(e) => field.onChange(e.target.value === "true")}
          error={!!errors[name]}
          helperText={errors[name]?.message}
          disabled={isLoading || locked}
          sx={formFieldSx}
        >
          <MenuItem value="true">{t("yes", "Yes")}</MenuItem>
          <MenuItem value="false">{t("no", "No")}</MenuItem>
        </TextField>
      )}
    />
  );

  /**
   * Render the base unit select — available options and locked state are
   * derived from the currently selected sellingMode.
   */
  const renderBaseUnitField = () => {
    const modeAllowed = getAllowedUnitsForSellingMode(selectedSellingMode);
    const allowedUnits = modeAllowed.filter((unit) =>
      allowedBaseUnits.includes(unit)
    );
    const locked = isBaseUnitLocked(selectedSellingMode);

    const options = allowedUnits.map((value) => ({
      value,
      label: UNIT_DISPLAY_LABELS[value]
    }));

    return (
      <Controller
        name="baseUnit"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            select
            fullWidth
            size={matchMD ? "small" : "medium"}
            label={t("add-product-base-unit", "Base Unit*")}
            value={field.value ?? ""}
            error={!!errors.baseUnit}
            helperText={errors.baseUnit?.message}
            disabled={isLoading || locked}
            sx={formFieldSx}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    );
  };

  const sellingModeOptions = allowedSellingModes
    .filter((value) =>
      Object.values(SellingMode).includes(value as SellingMode)
    )
    .map((value) => ({
      value,
      label: SELLING_MODE_DISPLAY_LABELS[value as SellingMode]
    }));

  const partialSellingLocked = !isPartialSellingPermitted(selectedSellingMode);

  const supplierPickerDisabled =
    isLoading ||
    supplierListPending ||
    (isCompanyAdmin && !supplierScopeStoreId);

  const quickAddIconButtonSx = {
    mt: 0.5,
    border: `1px solid ${BRAND_COLOR}`,
    borderRadius: 2,
    color: BRAND_COLOR,
    bgcolor: "background.paper",
    "&:hover": {
      borderColor: BRAND_HOVER,
      bgcolor: BRAND_HOVER,
      color: "#fff"
    },
    "&.Mui-disabled": {
      borderColor: "divider",
      color: "action.disabled",
      bgcolor: "action.disabledBackground"
    }
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={t("add-product-title", "Add New Product")}
      dataTestId="add-product-modal"
      icon={
        <AddCircleOutlineRoundedIcon
          sx={{ color: BRAND_COLOR, fontSize: 28 }}
        />
      }
      footer={
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            flexWrap: "wrap",
            width: "100%"
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                borderColor: "text.secondary",
                backgroundColor: "action.hover"
              }
            }}
          >
            {t("add-product-cancel", "Cancel")}
          </Button>
          <LoadingButton
            form="add-product-form"
            type="submit"
            variant="contained"
            loading={isLoading}
            size={matchMD ? "medium" : "large"}
            data-testid="add-product-submit"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              backgroundColor: BRAND_COLOR,
              boxShadow: "none",
              "&:hover": { backgroundColor: BRAND_HOVER, boxShadow: "none" }
            }}
          >
            {t("add-product-add-product", "Add Product")}
          </LoadingButton>
        </Box>
      }
    >
      <Box
        component="form"
        id="add-product-form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <AddProductGuidanceBanner />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {renderField(
            "name",
            t("add-product-product-name", "Product Name*"),
            t("add-product-name-placeholder", "e.g. BoomHigh Speaker"),
            { "data-testid": "product-name-input" }
          )}
          {renderSelectField(
            "sellingMode",
            t("add-product-selling-mode", "Selling Mode*"),
            sellingModeOptions
          )}
          {isCompanyAdmin && (
            <Controller
              name="storeId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("add-product-store", "Store*")}
                  value={field.value ?? ""}
                  error={!!errors.storeId}
                  helperText={errors.storeId?.message}
                  disabled={isLoading || storesLoading}
                  sx={formFieldSx}
                  data-testid="add-product-store-select"
                >
                  <MenuItem value="">
                    {storesLoading
                      ? t("add-product-store-loading", "Loading stores…")
                      : t("add-product-select-store", "Select store")}
                  </MenuItem>
                  {companyStores.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          )}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              width: "100%"
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    label={t("add-product-category", "Category*")}
                    error={!!errors.categoryId}
                    helperText={errors.categoryId?.message}
                    disabled={isLoading}
                    sx={formFieldSx}
                    data-testid="add-product-category-select"
                  >
                    <MenuItem value="">
                      {t("add-product-select-category", "Select category")}
                    </MenuItem>
                    {getLeafCategories(categoryList).map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Box>
            <Tooltip
              title={t(
                "add-product-add-category-tooltip",
                "Click to add new category"
              )}
            >
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignSelf: "flex-start",
                  mt: 0.5
                }}
              >
                <IconButton
                  onClick={() => setQuickAddCategoryOpen(true)}
                  disabled={isLoading}
                  aria-label={t("add-product-add-category", "Add new category")}
                  sx={{ ...quickAddIconButtonSx, mt: 0 }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Tooltip>
          </Box>
          {renderField(
            "brandName",
            t("add-product-brand-name", "Brand Name*"),
            "e.g. KivuNova"
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {!(isCompanyAdmin && !supplierScopeStoreId) && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
                width: "100%"
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Controller
                  name="supplierId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      size={matchMD ? "small" : "medium"}
                      label={t("add-product-supplier", "Supplier")}
                      error={!!errors.supplierId}
                      helperText={errors.supplierId?.message}
                      disabled={supplierPickerDisabled}
                      sx={formFieldSx}
                      data-testid="add-product-supplier-select"
                    >
                      <MenuItem value="">
                        {t("add-product-select-supplier", "Select supplier")}
                      </MenuItem>
                      {supplierList.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Box>
              <IconButton
                onClick={() => setQuickAddSupplierOpen(true)}
                disabled={supplierPickerDisabled}
                aria-label={t("add-product-add-supplier", "Add new supplier")}
                sx={quickAddIconButtonSx}
              >
                <AddIcon />
              </IconButton>
            </Box>
          )}
          {renderBaseUnitField()}
          {renderBooleanField(
            "isPartialSellingAllowed",
            t(
              "add-product-partial-selling-allowed",
              "Partial Selling Allowed*"
            ),
            partialSellingLocked
          )}
        </Box>

        {renderField(
          "description",
          t("add-product-description", "Description*"),
          t(
            "add-product-description-placeholder",
            "Describe the product clearly and avoid special characters like < > { }"
          )
        )}
      </Box>

      {quickAddSupplierOpen ? (
        <QuickAddSupplierModal
          open={quickAddSupplierOpen}
          onClose={() => setQuickAddSupplierOpen(false)}
          requireStoreId={isCompanyAdmin}
          storeIdForCreate={
            isCompanyAdmin ? watchedStoreId : userDetails?.storeId
          }
          onSupplierCreated={(s) => {
            setValue("supplierId", s.id, { shouldValidate: true });
            setQuickAddSupplierOpen(false);
          }}
        />
      ) : null}
      {quickAddCategoryOpen ? (
        <QuickAddCategoryModal
          open={quickAddCategoryOpen}
          onClose={() => setQuickAddCategoryOpen(false)}
          onCategoryCreated={async ({ id }) => {
            await getCategoryList
              .mutateAsync({ status: CATEGORY_STATUS.ACTIVE })
              .catch(() => {});
            setValue("categoryId", id, { shouldValidate: true });
            setQuickAddCategoryOpen(false);
          }}
        />
      ) : null}
    </FormModal>
  );
};

export default AddProductModal;
